import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Truck, Loader2, ShieldAlert, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO =
  "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBlocked(false);
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err?.code === "ROLE_BLOCKED") {
        setBlocked(true);
        setError(err.message);
      } else {
        setError(apiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark min-h-screen bg-slate-950 lg:grid lg:grid-cols-2">
      {/* Hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Centro de distribuição" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-orange-500 px-3 py-2">
            <Truck className="h-5 w-5 text-white" />
            <span className="font-bold text-white">Central do Courier</span>
          </div>
          <h2 className="max-w-md text-3xl font-bold leading-tight text-white">
            Console de Gestão e Administração
          </h2>
          <p className="mt-3 max-w-md text-sm text-slate-300">
            Auditoria de KM e entregas, estoque, benefícios, folha de pagamento e
            colaboradores — tudo em um só lugar.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex min-h-screen items-center justify-center p-6 lg:min-h-0">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="rounded-lg bg-orange-500 p-2">
              <Truck className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white">Central do Courier</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-50">Entrar no painel</h1>
          <p className="mt-1 text-sm text-slate-400">
            Acesso para <span className="text-orange-400 font-medium">Gestor</span> ou{" "}
            <span className="text-orange-400 font-medium">Administrativo</span>.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com"
                data-testid="login-email"
                className="border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-600"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password"
                  className="border-slate-700 bg-slate-900 pr-10 text-slate-100 placeholder:text-slate-600"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
                  blocked
                    ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                    : "border-rose-500/30 bg-rose-500/10 text-rose-200"
                }`}
              >
                {blocked && <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />}
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
